import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Mail, Loader2, ExternalLink, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OnboardingComplete() {
  const [params] = useSearchParams();
  const leadId = params.get("lead_id");
  const [state, setState] = useState({ status: "sending", error: null, data: null });

  useEffect(() => {
    if (!leadId) {
      setState({ status: "error", error: "Missing lead_id in URL.", data: null });
      return;
    }
    axios
      .post(`${API}/onboarding/complete`, { lead_id: leadId })
      .then((r) => setState({ status: "ok", error: null, data: r.data }))
      .catch((e) =>
        setState({
          status: "error",
          error: e?.response?.data?.detail || e?.message || "Email send failed.",
          data: null,
        })
      );
  }, [leadId]);

  return (
    <main
      className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6"
      data-testid="onboarding-complete-page"
    >
      <div className="max-w-xl w-full bg-white border border-slate-200 p-8 md:p-12">
        {state.status === "sending" && (
          <div className="flex flex-col items-start gap-4" data-testid="oc-sending">
            <Loader2 className="w-8 h-8 text-[#C9A349] animate-spin" />
            <h1 className="font-heading font-black text-3xl tracking-tight">
              Wrapping up your onboarding…
            </h1>
            <p className="text-slate-600">
              We're sending you a warm welcome email with your next step. One moment.
            </p>
          </div>
        )}

        {state.status === "ok" && (
          <div className="flex flex-col items-start gap-4" data-testid="oc-ok">
            <CheckCircle2 className="w-10 h-10 text-[#C9A349]" />
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
              You're all set
            </p>
            <h1 className="font-heading font-black text-4xl tracking-tight">
              Welcome aboard. 🎉
            </h1>
            <p className="text-slate-600">
              {state.data?.already_sent
                ? "Your welcome email has already been sent — please check your inbox."
                : "We've just sent a warm welcome email to "}
              {!state.data?.already_sent && (
                <strong>{state.data?.email}</strong>
              )}
              {!state.data?.already_sent && " — please check your inbox."}
            </p>
            <div className="w-full mt-2 border-t border-slate-200 pt-4 text-sm text-slate-600 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#C9A349]" />
              {state.data?.entity === "company" ? (
                <>Next step in the email: invite us as your <strong>Xero advisor</strong>.</>
              ) : (
                <>Next step in the email: accept your <strong>Prosaic invite</strong>.</>
              )}
            </div>
            <Link
              to="/"
              className="mt-4 inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-3 font-medium hover:bg-[#C9A349] transition-colors"
              data-testid="oc-home"
            >
              Back to homepage <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {state.status === "error" && (
          <div className="flex flex-col items-start gap-4" data-testid="oc-error">
            <div className="w-10 h-10 grid place-items-center bg-rose-100 text-rose-600">
              <ExternalLink className="w-5 h-5" />
            </div>
            <h1 className="font-heading font-black text-3xl tracking-tight">
              Form submitted — but the email didn't go through
            </h1>
            <p className="text-slate-600">
              Don't worry — your details are saved and our team will be in touch within one
              business day. Technical detail (for your records):{" "}
              <code className="text-xs">{state.error}</code>
            </p>
            <Link
              to="/"
              className="mt-2 inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-3 font-medium hover:bg-[#C9A349] transition-colors"
              data-testid="oc-home-error"
            >
              Back to homepage
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
